import { type FC, type ComponentProps, useRef, useCallback, useEffect, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { HpSharedFeaturedModuleControl } from './HpSharedFeaturedModuleControl';
import { HpSharedFeaturedModuleVariant } from './HpSharedFeaturedModuleVariant';

import { isSaleVariant, SALE_TEST_NAME } from '../helpers/abTestSale';
import { type HpSharedFeaturedModule_viewer$key } from './__generated__/HpSharedFeaturedModule_viewer.graphql';
import { type HpSharedFeaturedModule_user$key } from './__generated__/HpSharedFeaturedModule_user.graphql';
import { type HpSharedFeaturedModule_componentModule$key } from './__generated__/HpSharedFeaturedModule_componentModule.graphql';

const componentModuleFragment = graphql`
    fragment HpSharedFeaturedModule_componentModule on FeaturedModule {
        ...HpSharedFeaturedModuleControl_componentModule @skip(if: $isSaleVariant)
    }
`;

const viewerFragment = graphql`
    fragment HpSharedFeaturedModule_viewer on Viewer {
        ...HpSharedFeaturedModuleVariant_viewer @include(if: $isSaleVariant)
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModule_user on User {
        ...HpSharedFeaturedModuleVariant_user @include(if: $isSaleVariant)
    }
`;

export const HpSharedFeaturedModule: FC<{
    viewer: HpSharedFeaturedModule_viewer$key;
    user?: HpSharedFeaturedModule_user$key | null;
    componentModule: HpSharedFeaturedModule_componentModule$key;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
    isClient: boolean;
}> = ({
    viewer: viewerRef,
    user: userRef,
    componentModule: componentModuleRef,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
    isClient,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const elementRef = useRef<HTMLDivElement>(null);
    const [isVariant, setIsVariant] = useState<boolean | null>(null);

    useEffect(() => {
        setIsVariant(isSaleVariant());
    }, []);

    const onVisibilityChange: ComponentProps<typeof VisibilityTracker>['onVisibilityChange'] =
        useCallback(({ isVisible }) => {
            if (isVisible) {
                trackAbTestV2Variant(SALE_TEST_NAME);
            }
        }, []);

    //isClient ensures that the module is rendered only after client side refetch
    if (!isClient || isVariant === null) {
        return null;
    }

    return (
        <div ref={elementRef}>
            <VisibilityTracker elementRef={elementRef} onVisibilityChange={onVisibilityChange} />
            {isVariant ? (
                <HpSharedFeaturedModuleVariant viewer={viewer} user={user} />
            ) : (
                <HpSharedFeaturedModuleControl
                    componentModule={componentModule}
                    useLazyLoadImages={useLazyLoadImages}
                    moduleIndex={moduleIndex}
                    totalModules={totalModules}
                />
            )}
        </div>
    );
};
